import type {
  PlanOption,
  BillingInterval,
  PlanPricingTier,
} from '../../../gql/graphql'

import type { PlanCalculation } from '../types'
import { BillingIntervalValues } from '../types'

/**
 * Enhanced plan calculation that includes additional metadata
 */
export interface EnhancedPlanCalculation extends PlanCalculation {
  originalPrice?: number
  discountValue?: number
}

/**
 * Calculate pricing based on tiered pricing structure
 * @param pricingTiers - Array of pricing tiers with unit price and upper bounds
 * @param channelCount - Number of channels to calculate pricing for
 * @returns Calculated pricing information
 */
export function calculateTierPrice(
  pricingTiers: PlanPricingTier[],
  channelCount: number,
): PlanCalculation {
  if (!pricingTiers.length || channelCount <= 0) {
    return {
      pricePerChannel: 0,
      totalPrice: 0,
      basePrice: 0,
    }
  }

  let totalPrice = 0
  let remainingChannels = channelCount
  let currentTierPrice = 0
  let previousUpperBound = 0

  for (const tier of pricingTiers) {
    if (remainingChannels <= 0) break

    // Calculate how many channels fall within this tier
    const tierCapacity =
      tier.upperBound === Infinity
        ? Infinity
        : tier.upperBound - previousUpperBound
    const channelsInThisTier = Math.min(remainingChannels, tierCapacity)

    if (channelsInThisTier > 0) {
      // Add flat fee for this tier (once per tier consumed)
      const tierFlatFee = tier.flatFee ?? 0
      totalPrice += tierFlatFee

      // Add per-channel price for channels in this tier
      totalPrice += channelsInThisTier * tier.unitPrice

      currentTierPrice = tier.unitPrice
      remainingChannels -= channelsInThisTier
    }

    previousUpperBound =
      tier.upperBound === Infinity ? Infinity : tier.upperBound

    // If this tier has infinite capacity, we're done
    if (tier.upperBound === Infinity) break
  }

  // Handle remaining channels that exceed all tier bounds using the last tier
  if (remainingChannels > 0 && pricingTiers.length > 0) {
    const lastTier = pricingTiers[pricingTiers.length - 1]
    totalPrice += remainingChannels * lastTier.unitPrice
    currentTierPrice = lastTier.unitPrice
  }

  return {
    pricePerChannel: currentTierPrice,
    totalPrice,
    basePrice: totalPrice,
  }
}

/**
 * Calculate yearly savings compared to monthly billing
 * @param monthlyPrice - Monthly price for the same plan
 * @param yearlyPrice - Yearly price for the plan
 * @returns Savings amount (always non-negative)
 */
export function calculateYearlySavings(
  monthlyPrice: number,
  yearlyPrice: number,
): number {
  const annualMonthlyPrice = monthlyPrice * 12
  const savings = annualMonthlyPrice - yearlyPrice
  return Math.max(0, savings)
}

/**
 * Group plan options by plan ID and billing interval for easy lookup
 * @param planOptions - Array of plan options
 * @returns Nested object: planId -> billingInterval -> planOption
 */
export function groupPlansByIdAndInterval(
  planOptions: PlanOption[],
): Record<string, Partial<Record<BillingInterval, PlanOption>>> {
  return planOptions.reduce<
    Record<string, Partial<Record<BillingInterval, PlanOption>>>
  >((acc, plan) => {
    if (!acc[plan.planId]) {
      acc[plan.planId] = {}
    }
    acc[plan.planId][plan.planInterval] = plan
    return acc
  }, {})
}

/**
 * Calculate enhanced pricing information including yearly savings
 * @param planOptions - Array of plan options
 * @param channelCount - Number of channels
 * @returns Record of planId to enhanced calculations
 */
export function calculateAllPlanPricing(
  planOptions: PlanOption[],
  channelCount: number,
  billingCycle: BillingInterval,
): Record<string, EnhancedPlanCalculation> {
  const result: Record<string, EnhancedPlanCalculation> = {}
  const plansByIdAndInterval = groupPlansByIdAndInterval(planOptions)
  const planOptionsForInterval = planOptions.filter(
    (plan) => plan.planInterval === billingCycle,
  )

  planOptionsForInterval.forEach((planOption) => {
    const baseCalculation = calculateTierPrice(planOption.pricing, channelCount)

    let enhancedCalculation: EnhancedPlanCalculation = {
      ...baseCalculation,
    }

    // Calculate savings for yearly plans
    if (planOption.planInterval === BillingIntervalValues.year) {
      const monthlyPlan =
        plansByIdAndInterval[planOption.planId]?.[BillingIntervalValues.month]

      if (monthlyPlan) {
        const monthlyCalculation = calculateTierPrice(
          monthlyPlan.pricing,
          channelCount,
        )
        const yearlySavings = calculateYearlySavings(
          monthlyCalculation.totalPrice,
          baseCalculation.totalPrice,
        )

        enhancedCalculation = {
          ...enhancedCalculation,
          savings: yearlySavings,
        }
      }
    }

    result[planOption.planId] = enhancedCalculation
  })

  return result
}

/**
 * Format a price for display with currency symbol and proper formatting
 * @param price - Price in the smallest currency unit (e.g., cents)
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @returns Formatted price string
 */
export function formatPrice(price: number, currency = 'USD'): string {
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : undefined
  const actualPrice = (price ?? 0) / 100

  // Use 2 decimals for non-integer prices (e.g., $7.50), 0 for integers (e.g., $10)
  // Both min and max must be set to ensure $7.5 displays as $7.50, not $7.5
  const fractionDigits = Number.isInteger(actualPrice) ? 0 : 2

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(actualPrice)
}

/**
 * Format a price without dividing by 100 (for prices already in main currency unit)
 * @param price - Price in main currency unit (e.g., dollars)
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @returns Formatted price string
 */
export function formatPriceSimple(price: number, currency = 'USD'): string {
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : undefined
  const actualPrice = price ?? 0

  // Use 2 decimals for non-integer prices (e.g., $7.50), 0 for integers (e.g., $10)
  // Both min and max must be set to ensure $7.5 displays as $7.50, not $7.5
  const fractionDigits = Number.isInteger(actualPrice) ? 0 : 2

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(actualPrice)
}
